import {
    PauseRounded,
    PlayArrowRounded,
    ReplayRounded,
    VolumeOffRounded,
    VolumeUpRounded,
} from '@mui/icons-material'
import { Box, BoxProps, IconButton, Stack } from '@mui/material'
import { useCallback, useContext, useEffect, useReducer, useRef } from 'react'
import ReactPlayer from 'react-player'
import { getRestApiConfig } from '../../../api/config'
import { ActivationObjectContext } from '../../providers/ActivationObjectProvider/constants'

interface VideoState {
    playing: boolean
    muted: boolean
}

interface PlayerAction {
    type: 'PLAY' | 'PAUSE' | 'MUTE' | 'UNMUTE'
    payload?: boolean
}

interface PlayerProps {
    videoName?: string
    shouldPlay?: boolean
}

type AvatarPlayerProps = PlayerProps & BoxProps

export const AvatarPlayer = ({
    videoName,
    shouldPlay = true,
    ...restProps
}: AvatarPlayerProps) => {
    const { globalAvatarMuted, setGlobalAvatarMuted } = useContext(
        ActivationObjectContext,
    )

    const restApiConfig = getRestApiConfig()
    const videoUrl = new URL(
        `/api/v1/videos?videoName=${videoName}`,
        restApiConfig.baseUrl,
    ).href

    const videoRef = useRef<ReactPlayer>(null)

    const playerReducer = (
        state: VideoState,
        action: PlayerAction,
    ): VideoState => {
        switch (action.type) {
            case 'PLAY':
            case 'PAUSE':
                return { ...state, playing: action.payload ?? state.playing }
            case 'MUTE':
            case 'UNMUTE':
                return { ...state, muted: action.payload ?? state.muted }
            default:
                return state
        }
    }

    const [playerState, playerDispatch] = useReducer(playerReducer, {
        playing: shouldPlay,
        muted: globalAvatarMuted,
    })
    const { playing, muted } = playerState

    const handlePlayPause = useCallback(() => {
        playerDispatch({
            type: playing ? 'PLAY' : 'PAUSE',
            payload: !playing,
        })
    }, [playing])

    const handleMuteUnmute = useCallback(() => {
        playerDispatch({
            type: muted ? 'MUTE' : 'UNMUTE',
            payload: !muted,
        })
        setGlobalAvatarMuted(!muted)
    }, [muted, setGlobalAvatarMuted])

    useEffect(() => {
        shouldPlay
            ? playerDispatch({
                  type: 'PLAY',
                  payload: shouldPlay,
              })
            : playerDispatch({
                  type: 'PAUSE',
                  payload: shouldPlay,
              })
    }, [shouldPlay])

    return videoName ? (
        <Box {...restProps}>
            <ReactPlayer
                ref={videoRef}
                url={videoUrl}
                className='mb-3'
                width={'100%'}
                height={'32rem'}
                playing={playing}
                muted={muted}
            >
                Your browser does not support the video tag.
            </ReactPlayer>
            <Stack
                direction='row'
                justifyContent='center'
                className='gap-4'
            >
                <IconButton onClick={handleMuteUnmute}>
                    {muted ? <VolumeOffRounded /> : <VolumeUpRounded />}
                </IconButton>
                <IconButton onClick={handlePlayPause}>
                    {playing ? <PauseRounded /> : <PlayArrowRounded />}
                </IconButton>
                <IconButton onClick={() => videoRef.current?.seekTo(0)}>
                    <ReplayRounded />
                </IconButton>
            </Stack>
        </Box>
    ) : null
}
